/**
 * Spring — the bounce block (Brief 009).
 *
 * A SOLID, tile-aligned 1×1 entity. When the player LANDS on its top (World's solid-
 * resolution reports a TOP hit with downward velocity), it launches the player upward
 * at `props.power` px/s and compresses its sprite for a beat. Not bonkable — a head-bonk
 * from below just stops the player (no consequence).
 *
 * Reuses the stomp-bounce PATH (set player.vy negative), at a much stronger magnitude.
 * A small landing-velocity threshold distinguishes a real fall (launch) from the tiny
 * per-frame gravity nibble while merely resting on top (no launch → no jitter loop).
 *
 * Determinism: min arithmetic + a dt-driven compress timer only → ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

/** Default launch velocity (px/s upward) when `props.power` is absent. ~1.5× a jump. */
const DEFAULT_POWER = 1100;
/** Min downward landing speed (px/s) to count as a real landing (not resting jitter). */
const LAUNCH_THRESHOLD = 120;
/** Seconds the compressed sprite shows after a launch (render-only). */
const COMPRESS_SECONDS = 0.16;

export class Spring implements Entity {
  readonly type = EntityType.SPRING;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private power: number;
  private compressTimer = 0;
  /** Brief 014 P1 (`props.variant:'note'`): the Mario Note Block reskin — same top-launch
   *  behaviour, distinct art. Absent ⇒ the classic spring. */
  private variant: string;

  /** @param props `power` (px/s upward launch; default 1100), `variant` ('note') art reskin. */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const p = props?.power;
    this.power = typeof p === 'number' && p > 0 ? p : DEFAULT_POWER;
    this.variant = typeof props?.variant === 'string' ? props.variant : 'spring';
  }

  /** Always solid — player stands on / collides with it like a block. */
  solidBody(): AABB { return this.body; }

  update(dt: number, _ctx: EntityContext): void {
    if (this.compressTimer > 0) this.compressTimer = Math.max(0, this.compressTimer - dt);
  }

  /** Top-landing → launch. `landingVy` is the player's downward speed at contact. */
  onStand(ctx: EntityContext, landingVy: number): void {
    if (landingVy < LAUNCH_THRESHOLD) return; // resting, not landing → no launch
    ctx.player.vy = -this.power;
    this.compressTimer = COMPRESS_SECONDS;
    ctx.playSound('spring', 0.5); // POLISH_01 #1 — dedicated spring boing (was 'jump')
    ctx.emitFx?.('dust', this.body.x + this.body.w / 2, this.body.y + this.body.h, { count: 4 }); // launch puff
  }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — acts via onStand */ }

  spriteKey(): string {
    if (this.variant === 'note') return 'spring_note';
    return this.compressTimer > 0 ? 'spring_down' : 'spring';
  }

  visible(): boolean { return true; }
}
